import { cn } from '@qr-front/ui/utils'
import { useAtomValue } from 'jotai'
import NextImage from 'next/image'

import { TUploadedMedia } from '@/services'

import {
	imageListAtom,
	imageLocalPreviewUrlsAtom,
} from '../../../contentFormSections'
import { facebookPrimaryColorAtom } from './facebookScreenState'

const IMAGE_ORDER_TO_SIZE = {
	0: { width: 166, height: 240 },
	1: { width: 204, height: 134 },
	2: { width: 204, height: 134 },
}

const Image = ({
	image,
	localSrc,
	showOverlay,
	order,
	demoMode,
}: {
	image?: TUploadedMedia
	localSrc?: string | null
	showOverlay: () => void
	order: 0 | 1 | 2
	demoMode: boolean
}) => {
	const demoImage = (
		<NextImage
			src="/web-app/qr-preview-demos/facebook/facebook-demo-image.webp"
			{...IMAGE_ORDER_TO_SIZE[order]}
			loading="lazy"
			alt="demo image"
			className="h-full w-full rounded-4 object-cover"
		/>
	)

	const imageSrc = image?.publicId
		? `${image.bucketRootUrl}/${image.publicId}`
		: localSrc

	const actualImage = imageSrc ? (
		// eslint-disable-next-line @next/next/no-img-element
		<img
			alt={image?.publicId ?? 'uploading'}
			src={imageSrc}
			onClick={showOverlay}
			className="h-full w-full rounded-4 object-cover"
		/>
	) : null

	const displayedImage = demoMode ? demoImage : actualImage

	return (
		<button
			onClick={() => {
				if (actualImage) {
					showOverlay()
				}
			}}
			className={cn(
				'rounded-4 bg-white p-[2px] shadow-generator [grid-column:1] [grid-row:1]',
				!imageSrc && 'pointer-events-none',
				(order === 0 && 'z-[1] h-60 w-[170px]') ||
					(order === 1 && 'h-[204px] w-[134px] translate-x-[36px]') ||
					(order === 2 && 'h-[204px] w-[134px] translate-x-[-36px]'),
			)}
		>
			{displayedImage}
		</button>
	)
}

export const CarouselPreview = ({
	demoMode,
	showOverlay,
}: {
	demoMode: boolean
	showOverlay: () => void
}) => {
	const imageList = useAtomValue(imageListAtom)
	const localPreviewUrls = useAtomValue(imageLocalPreviewUrlsAtom)

	const firstImage = imageList[0]
	const secondImage = imageList[1]
	const thirdImage = imageList[2]

	const primaryColor = useAtomValue(facebookPrimaryColorAtom)

	return (
		<div className="relative grid place-items-center pt-6">
			<div
				className="absolute top-0 h-[150px] w-full transition-colors"
				style={{ backgroundColor: primaryColor }}
			/>
			<Image
				image={firstImage}
				localSrc={localPreviewUrls[0]}
				showOverlay={showOverlay}
				order={0}
				demoMode={demoMode}
			/>
			{secondImage || localPreviewUrls[1] ? (
				<Image
					image={secondImage}
					localSrc={localPreviewUrls[1]}
					showOverlay={showOverlay}
					order={1}
					demoMode={demoMode}
				/>
			) : null}
			{thirdImage || localPreviewUrls[2] ? (
				<Image
					image={thirdImage}
					localSrc={localPreviewUrls[2]}
					showOverlay={showOverlay}
					order={2}
					demoMode={demoMode}
				/>
			) : null}
		</div>
	)
}
